/**
 * MapControls - Navigation controls for the map (zoom, recenter, compass)
 */

import { motion } from 'framer-motion';
import { useNavigationStore } from '@/stores/useNavigationStore';
import { useLocationStore } from '@/stores';
import { cn } from '@/utils/cn';

interface MapControlsProps {
  className?: string;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onRecenter?: () => void;
  onCompassClick?: () => void;
  heading?: number;
}

export function MapControls({
  className,
  onZoomIn,
  onZoomOut,
  onRecenter,
  onCompassClick,
  heading = 0,
}: MapControlsProps) {
  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const currentPosition = useLocationStore((state) => state.currentPosition);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Compass */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={onCompassClick}
        className={cn(
          'w-11 h-11 rounded-full',
          'bg-glass-bg border border-glass-border',
          'backdrop-blur-md shadow-lg',
          'flex items-center justify-center',
          'hover:bg-hud-primary/10 transition-colors'
        )}
        title="Reset compass"
      >
        <motion.div
          animate={{ rotate: -heading }}
          transition={{ type: 'spring', stiffness: 200 }}
        >
          <CompassIcon className="w-5 h-5" />
        </motion.div>
      </motion.button>

      {/* Zoom Controls */}
      <div className="flex flex-col bg-glass-bg border border-glass-border rounded-lg backdrop-blur-md overflow-hidden">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onZoomIn}
          className={cn(
            'w-11 h-10 flex items-center justify-center',
            'hover:bg-hud-primary/10 transition-colors',
            'border-b border-glass-border'
          )}
          title="Zoom in"
        >
          <PlusIcon className="w-5 h-5 text-text-primary" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onZoomOut}
          className={cn(
            'w-11 h-10 flex items-center justify-center',
            'hover:bg-hud-primary/10 transition-colors'
          )}
          title="Zoom out"
        >
          <MinusIcon className="w-5 h-5 text-text-primary" />
        </motion.button>
      </div>

      {/* Recenter Button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={onRecenter}
        className={cn(
          'w-11 h-11 rounded-full',
          'bg-glass-bg border border-glass-border',
          'backdrop-blur-md shadow-lg',
          'flex items-center justify-center',
          'hover:bg-hud-primary/10 transition-colors',
          currentPosition && 'border-hud-primary/50'
        )}
        title="Recenter on location"
      >
        <RecenterIcon className="w-5 h-5 text-hud-primary" />
      </motion.button>

      {/* Speed Indicator (during navigation) */}
      {isNavigating && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className={cn(
            'w-14 h-14 rounded-full',
            'bg-bg-deep border-2 border-glass-border',
            'flex flex-col items-center justify-center',
            'shadow-lg'
          )}
        >
          <span className="text-lg font-bold text-text-primary">0</span>
          <span className="text-[8px] text-text-secondary uppercase">km/h</span>
        </motion.div>
      )}
    </div>
  );
}

// Icons
function CompassIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      {/* North pointer (red) */}
      <path
        d="M12 2L9 12h6L12 2z"
        fill="var(--hud-danger)"
      />
      {/* South pointer (white) */}
      <path
        d="M12 22l3-10H9l3 10z"
        fill="var(--text-primary)"
      />
      {/* Center dot */}
      <circle cx="12" cy="12" r="2" fill="var(--glass-border)" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function MinusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M5 12h14" />
    </svg>
  );
}

function RecenterIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </svg>
  );
}

export default MapControls;
